import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * Shared PDF text search for the canvas-based pdfjs viewers (FileViewerPanel,
 * FileViewerModal). Pages render to bare canvases with no text layer, so the
 * browser's own Ctrl+F can never match PDF content — this extracts per-page
 * text via getTextContent, finds matches, scrolls to them, and exposes rects
 * for highlight overlays.
 *
 * Match rects are stored in scale-1 viewport units and multiplied by the
 * current scale at render time, so zooming never invalidates them.
 *
 * Contract with the host viewer:
 *  - pages are wrapped in elements carrying data-page="N" inside scrollContainerRef
 *  - the page wrapper is position:relative so <PdfPageHighlights> can overlay it
 */
export function usePdfSearch({ pdfDocument, totalPages, pdfScale, scrollContainerRef, enabled = true }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState([]); // [{page, left, top, width, height}]
  const [currentMatch, setCurrentMatch] = useState(0);
  const [searchRunning, setSearchRunning] = useState(false);
  const pageTextRef = useRef({}); // pageNum -> [{str, left, top, width, height}] at scale 1
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const pdfScaleRef = useRef(pdfScale);
  pdfScaleRef.current = pdfScale;

  // Reset when a new document loads
  useEffect(() => {
    pageTextRef.current = {};
    setSearchMatches([]);
    setCurrentMatch(0);
    setSearchQuery('');
    setSearchOpen(false);
  }, [pdfDocument]);

  const getPageTextItems = async (pageNum) => {
    if (pageTextRef.current[pageNum]) return pageTextRef.current[pageNum];
    const page = await pdfDocument.getPage(pageNum);
    const vp1 = page.getViewport({ scale: 1 });
    const tc = await page.getTextContent();
    const items = tc.items
      .filter(it => it.str && it.str.trim())
      .map(it => {
        const t = pdfjsLib.Util.transform(vp1.transform, it.transform);
        const fontHeight = Math.hypot(t[2], t[3]);
        return { str: it.str, left: t[4], top: t[5] - fontHeight, width: it.width, height: fontHeight };
      });
    pageTextRef.current[pageNum] = items;
    return items;
  };

  const scrollToMatch = (m) => {
    const container = scrollContainerRef.current;
    const wrapper = container ? container.querySelector(`[data-page="${m.page}"]`) : null;
    if (!container || !wrapper) return;
    container.scrollTop = Math.max(0, wrapper.offsetTop + m.top * pdfScaleRef.current - container.clientHeight / 3);
  };

  const goToMatch = (delta) => {
    setCurrentMatch(prev => {
      if (searchMatches.length === 0) return prev;
      const next = (prev + delta + searchMatches.length) % searchMatches.length;
      scrollToMatch(searchMatches[next]);
      return next;
    });
  };

  // Debounced search across all pages. Char-fraction offsets tighten the
  // highlight to the matched substring within a text chunk (approximation —
  // assumes roughly uniform glyph width within the chunk).
  useEffect(() => {
    if (!enabled || !pdfDocument) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = searchQuery.trim().toLowerCase();
    if (!q) { setSearchMatches([]); setCurrentMatch(0); setSearchRunning(false); return; }
    let cancelled = false;
    setSearchRunning(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const found = [];
        for (let p = 1; p <= totalPages; p++) {
          const items = await getPageTextItems(p);
          if (cancelled) return;
          for (const it of items) {
            const lower = it.str.toLowerCase();
            let idx = lower.indexOf(q);
            while (idx !== -1) {
              const startFrac = idx / it.str.length;
              const widthFrac = q.length / it.str.length;
              found.push({
                page: p,
                left: it.left + it.width * startFrac,
                top: it.top,
                width: Math.max(6, it.width * widthFrac),
                height: it.height,
              });
              idx = lower.indexOf(q, idx + q.length);
            }
          }
        }
        if (cancelled) return;
        setSearchMatches(found);
        setCurrentMatch(0);
        setSearchRunning(false);
        if (found.length > 0) scrollToMatch(found[0]);
      } catch (_) {
        if (!cancelled) setSearchRunning(false);
      }
    }, 250);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, pdfDocument, totalPages, enabled]);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) { searchInputRef.current.focus(); searchInputRef.current.select(); }
    }, 0);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchMatches([]);
    setCurrentMatch(0);
  };

  // Ctrl/Cmd+F opens the in-viewer search instead of the (useless) browser find
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key || '').toLowerCase() === 'f' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); goToMatch(e.shiftKey ? -1 : 1); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeSearch(); }
  };

  return {
    searchOpen, openSearch, closeSearch,
    searchQuery, setSearchQuery,
    searchMatches, currentMatch, searchRunning,
    goToMatch, searchInputRef, onSearchKeyDown,
  };
}

/** Toolbar cluster: collapsed magnifier button, expands to the search bar. */
export function PdfSearchControl({ search }) {
  const {
    searchOpen, openSearch, closeSearch,
    searchQuery, setSearchQuery,
    searchMatches, currentMatch, searchRunning,
    goToMatch, searchInputRef, onSearchKeyDown,
  } = search;

  if (!searchOpen) {
    return (
      <button onClick={openSearch}
        className="p-1.5 bg-white/90 backdrop-blur rounded-lg shadow text-gray-600 hover:text-gray-900"
        title="Search (Ctrl+F)">
        <Search size={16} />
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 bg-white/95 backdrop-blur rounded-lg shadow px-2 py-1">
      <Search size={14} className="text-gray-400 flex-shrink-0" />
      <input
        ref={searchInputRef}
        type="text"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        onKeyDown={onSearchKeyDown}
        placeholder="Search PDF…"
        className="w-40 text-xs text-gray-800 bg-transparent outline-none placeholder-gray-400"
      />
      <span className="text-[11px] text-gray-500 whitespace-nowrap min-w-[44px] text-center">
        {searchRunning ? '…' : searchQuery.trim() ? `${searchMatches.length === 0 ? 0 : currentMatch + 1}/${searchMatches.length}` : ''}
      </span>
      <button onClick={() => goToMatch(-1)} disabled={searchMatches.length === 0}
        className="p-0.5 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100 disabled:opacity-40"
        title="Previous match (Shift+Enter)">
        <ChevronUp size={14} />
      </button>
      <button onClick={() => goToMatch(1)} disabled={searchMatches.length === 0}
        className="p-0.5 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100 disabled:opacity-40"
        title="Next match (Enter)">
        <ChevronDown size={14} />
      </button>
      <button onClick={closeSearch}
        className="p-0.5 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100" title="Close (Esc)">
        <X size={14} />
      </button>
    </div>
  );
}

/** Highlight overlays for one page. Render inside the position:relative page wrapper. */
export function PdfPageHighlights({ search, page, scale }) {
  const { searchMatches, currentMatch } = search;
  return (
    <>
      {searchMatches.map((m, gi) => m.page === page ? (
        <div key={gi}
          className="absolute pointer-events-none rounded-[1px]"
          style={{
            left: m.left * scale - 1,
            top: m.top * scale - 1,
            width: m.width * scale + 2,
            height: m.height * scale + 2,
            backgroundColor: gi === currentMatch ? 'rgba(249, 115, 22, 0.45)' : 'rgba(250, 204, 21, 0.4)',
            outline: gi === currentMatch ? '1.5px solid rgb(249, 115, 22)' : 'none',
            mixBlendMode: 'multiply',
          }} />
      ) : null)}
    </>
  );
}
